import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest, getStoreIdFromToken } from '../../utils/api';
import backIcon from '../../assets/icons/common/back_icon.png';

/**
 * 상품별 출력 설정 — 프린터·UI·POS전송 3축을 메뉴별로 제어. 카테고리별 그룹 + 전체 일괄 O/X.
 *  · 자동포스팅 = POS 프로그램 전송 여부 (receipt_yn 의 반대). 와우포스는 POS 전송 개념이 없어 컬럼은 두되 셀은 비움(토글 없음).
 *  · UI         = 사장님 PC 클라이언트 카드/사이드패널 표시 여부 (show_in_ui).
 *  · 프린터     = 프린터 매핑 존재 여부 (읽기전용 — 프린터 설정에서만 변경).
 * 상단 고정 2행(호출/리뷰): 자동포스팅 X 고정, UI=staff_call_ui/review_ui, 프린터=메인프린터 존재.
 *
 * 저장은 await-then-commit — 서버 응답 성공 후에만 화면을 반영한다(실패 시 원복+토스트, M-A).
 */
const MenuOutputSettings = ({ isOpen, onClose, posType }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [storeId, setStoreId] = useState(null);
  const [categories, setCategories] = useState([]); // [{ category_code, category_name, menus: [{menu_key, menu_name, receipt_yn, show_in_ui}] }]
  const [printers, setPrinters] = useState([]); // 사용중(in_use) 프린터 = 동적 컬럼
  const [menuPrinterMap, setMenuPrinterMap] = useState(new Map()); // menu_key(String) → Set<printer_id>
  const [reviewUi, setReviewUi] = useState(true);
  const [staffCallUi, setStaffCallUi] = useState(true);
  const [savingKeys, setSavingKeys] = useState(new Set()); // 개별 중복 저장 방지
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimerRef = useRef(null);

  const isWowpos = posType === 'wowpos';

  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: true, message, type });
    toastTimerRef.current = setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 2000);
  };

  const loadAll = useCallback(async (sid) => {
    if (!sid) return;
    setIsLoading(true);
    try {
      const [poolResp, mapResp, uiResp, prnResp] = await Promise.all([
        apiRequest(`/stores/${sid}/menu-pool`).catch(() => null), // grouped (카테고리별)
        apiRequest(`/stores/${sid}/printer-menu-mappings`).catch(() => null),
        apiRequest(`/store-settings/${sid}/output-ui-settings`).catch(() => null),
        apiRequest(`/stores/${sid}/printers`).catch(() => null),
      ]);
      if (poolResp?.success) setCategories(Array.isArray(poolResp.categories) ? poolResp.categories : []);
      // 사용중(in_use) 프린터만 컬럼. display_order/id 순.
      if (prnResp?.success) setPrinters((prnResp.printers || []).filter((p) => p.in_use));
      // menu_key(String) → Set<printer_id>. ⚠ 키·조회 양쪽 String() 강제 (wowpos 숫자키 대비).
      if (mapResp?.success) {
        const m = new Map();
        (mapResp.mappings || []).forEach((row) => {
          const k = String(row.menu_key);
          if (!m.has(k)) m.set(k, new Set());
          m.get(k).add(row.printer_id);
        });
        setMenuPrinterMap(m);
      }
      if (uiResp?.success) {
        setReviewUi(uiResp.reviewUi !== false);
        setStaffCallUi(uiResp.staffCallUi !== false);
      }
    } catch (e) {
      console.error('[menuOutputSettings] load 실패:', e);
      showToast('설정을 불러오지 못했습니다', 'error');
    }
    setIsLoading(false);
  }, []);

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setIsAnimating(false);
      const t = setTimeout(() => setIsVisible(false), 220);
      return () => clearTimeout(t);
    }
    setIsVisible(true);
    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setIsAnimating(true));
    });
    (async () => {
      try {
        const sid = getStoreIdFromToken();
        setStoreId(sid);
        await loadAll(sid);
      } catch (e) {
        console.error('[menuOutputSettings] storeId 조회 실패:', e);
        showToast('매장 정보를 불러오지 못했습니다', 'error');
        setIsLoading(false);
      }
    })();
    // history entry 쌓아서 뒤로가기 1회는 이 페이지만 닫고 storeSettings 는 유지 (printerManager 패턴)
    window.history.pushState({ menuOutputSettings: true }, '');
    const handlePop = () => {
      if (window.history.state?.menuOutputSettings) return; // 하위 modal pop → 무시
      if (typeof onCloseRef.current === 'function') onCloseRef.current();
    };
    window.addEventListener('popstate', handlePop);
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      window.removeEventListener('popstate', handlePop);
      if (window.history.state?.menuOutputSettings) {
        window.history.back();
      }
    };
  }, [isOpen, loadAll]);

  const markSaving = (key, on) => {
    setSavingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  // 특정 menu_key 의 필드를 전 카테고리에서 갱신 (okdc 는 한 메뉴가 여러 카테고리에 등장)
  const patchMenu = (menuKey, patch) => {
    setCategories((prev) => prev.map((cat) => ({
      ...cat,
      menus: (cat.menus || []).map((m) => (String(m.menu_key) === String(menuKey) ? { ...m, ...patch } : m)),
    })));
  };

  // 자동포스팅 토글 = receipt_yn 반전 (자동포스팅 O = receipt_yn false). 와우포스는 잠금.
  const toggleAutopost = async (menu) => {
    if (isWowpos) return;
    const key = `ap:${menu.menu_key}`;
    if (savingKeys.has(key)) return;
    const newReceiptYn = !menu.receipt_yn;
    markSaving(key, true);
    try {
      await apiRequest(`/menus/${storeId}/bulk-receipt`, {
        method: 'PUT',
        body: JSON.stringify({ menu_keys: [menu.menu_key], receipt_yn: newReceiptYn }),
      });
      patchMenu(menu.menu_key, { receipt_yn: newReceiptYn });
    } catch (e) {
      showToast('저장에 실패했습니다', 'error');
    }
    markSaving(key, false);
  };

  // UI 토글 = show_in_ui 반전.
  const toggleShowInUi = async (menu) => {
    const key = `ui:${menu.menu_key}`;
    if (savingKeys.has(key)) return;
    const newShow = !(menu.show_in_ui !== false);
    markSaving(key, true);
    try {
      await apiRequest(`/menus/${storeId}/bulk-show-in-ui`, {
        method: 'PUT',
        body: JSON.stringify({ menu_keys: [menu.menu_key], show_in_ui: newShow }),
      });
      patchMenu(menu.menu_key, { show_in_ui: newShow });
    } catch (e) {
      showToast('저장에 실패했습니다', 'error');
    }
    markSaving(key, false);
  };

  // 호출/리뷰 UI 토글 (per-store) — output-ui-settings 전용 PUT.
  const toggleStoreUi = async (which) => {
    const key = `store:${which}`;
    if (savingKeys.has(key)) return;
    const cur = which === 'review' ? reviewUi : staffCallUi;
    const nextVal = !cur;
    const bodyKey = which === 'review' ? 'reviewUi' : 'staffCallUi';
    markSaving(key, true);
    try {
      await apiRequest(`/store-settings/${storeId}/output-ui-settings`, {
        method: 'PUT',
        body: JSON.stringify({ [bodyKey]: nextVal }),
      });
      if (which === 'review') setReviewUi(nextVal); else setStaffCallUi(nextVal);
    } catch (e) {
      showToast('저장에 실패했습니다', 'error');
    }
    markSaving(key, false);
  };

  // 메뉴 ↔ 프린터 매핑 토글 (프린터별 O/X, 다중 허용). POST(추가)/DELETE(해제) → menuPrinterMap 갱신.
  const toggleMenuPrinter = async (menu, printer) => {
    const mkey = String(menu.menu_key);
    const key = `mp:${mkey}:${printer.id}`;
    if (savingKeys.has(key)) return;
    const has = menuPrinterMap.get(mkey)?.has(printer.id);
    markSaving(key, true);
    try {
      if (has) {
        await apiRequest(`/stores/${storeId}/printers/${printer.id}/menus/${encodeURIComponent(mkey)}`, { method: 'DELETE' });
      } else {
        await apiRequest(`/stores/${storeId}/printers/${printer.id}/menus`, { method: 'POST', body: JSON.stringify({ menu_key: mkey }) });
      }
      setMenuPrinterMap((prev) => {
        const next = new Map(prev);
        const set = new Set(next.get(mkey) || []);
        if (has) set.delete(printer.id); else set.add(printer.id);
        next.set(mkey, set);
        return next;
      });
    } catch (e) {
      showToast('저장에 실패했습니다', 'error');
    }
    markSaving(key, false);
  };

  // 프린터별 호출/리뷰 인쇄 토글. PUT /printers/{id}/print-flags → printers 갱신.
  const togglePrinterFlag = async (printer, which) => {
    const field = which === 'review' ? 'print_review' : 'print_call';
    const key = `pf:${printer.id}:${which}`;
    if (savingKeys.has(key)) return;
    const nextVal = !printer[field];
    markSaving(key, true);
    try {
      await apiRequest(`/stores/${storeId}/printers/${printer.id}/print-flags`, {
        method: 'PUT', body: JSON.stringify({ [field]: nextVal }),
      });
      setPrinters((prev) => prev.map((p) => (p.id === printer.id ? { ...p, [field]: nextVal } : p)));
    } catch (e) {
      showToast('저장에 실패했습니다', 'error');
    }
    markSaving(key, false);
  };

  if (!isVisible) return null;

  // 동적 grid — 메뉴명(가변) | 자동포스팅 | UI | 사용중 프린터 N개. styled-jsx 정적CSS로는 런타임 N 불가라 인라인.
  // 스크롤 기준 = "데이터 컬럼(메뉴명 제외: 자동포스팅+UI+프린터) 4개 이상".
  //   데이터 3개 이하(프린터 1개 이하): 메뉴명 minmax(0,1fr)로 화면에 맞게 축소 → 가로 스크롤 없음.
  //   데이터 4개 이상(프린터 2개 이상): 메뉴명 160px 유지 + min-width로 가로 스크롤(메뉴명 좌측 고정).
  // 자동포스팅 컬럼은 항상 표시(헤더 노출). 와우포스는 POS 전송 개념이 없어 셀만 비운다(토글 없음).
  const COL_W = 70; // 자동포스팅/UI/프린터 동일 폭
  const dataCols = 2 + printers.length; // 자동포스팅 + UI + 프린터N (메뉴명 제외)
  const colCount = 1 + dataCols; // 메뉴명 + 데이터 컬럼
  const scrollable = dataCols >= 4; // 데이터 컬럼(메뉴 제외) 4개 이상이면 가로 스크롤(메뉴명 유지)
  const menuCol = scrollable ? 'minmax(160px, 1fr)' : 'minmax(0, 1fr)';
  const gridCols = `${menuCol} repeat(${dataCols}, ${COL_W}px)`;
  const tableMinWidth = 160 + COL_W * dataCols + (colCount - 1) * 4 + 8;

  // O/X 셀 렌더 — clickable: 버튼, locked/readonly: 텍스트
  const Cell = ({ on, onClick, locked, readonly }) => {
    const label = on ? 'O' : 'X';
    if (onClick && !locked && !readonly) {
      return (
        <button className={`mos-cell mos-clickable ${on ? 'on' : 'off'}`} onClick={onClick}>{label}</button>
      );
    }
    return <span className={`mos-cell ${readonly ? 'mos-readonly' : 'mos-locked'} ${on ? 'on' : 'off'}`}>{label}</span>;
  };

  const totalMenus = categories.reduce((n, c) => n + (c.menus?.length || 0), 0);

  return (
    <div className={`mos-overlay ${isAnimating ? 'open' : ''}`}>
      <div className="mos-sheet">
        <div className="mos-header">
          <button className="mos-back" onClick={() => onClose && onClose()}>
            <img src={backIcon} alt="back" />
          </button>
          <span className="mos-title">상품별 출력 설정</span>
        </div>

        {isLoading ? (
          <div className="mos-loading">불러오는 중…</div>
        ) : (
          <div className="mos-tablewrap">
           <div className={`mos-table ${scrollable ? 'mos-scroll' : ''}`} style={{ minWidth: scrollable ? tableMinWidth + 'px' : undefined }}>
            <div className="mos-row mos-head" style={{ gridTemplateColumns: gridCols }}>
              <span className="mos-name"><span className="mos-nm-in">메뉴</span></span>
              <span className="mos-col">자동포스팅</span>
              <span className="mos-col">UI</span>
              {printers.map((pr) => (
                <span className="mos-col" key={`h-${pr.id}`} title={pr.name}>{pr.alias || pr.name}</span>
              ))}
            </div>

            {/* 상단 고정: 호출 / 리뷰 — 프린터 컬럼은 프린터별 호출/리뷰 인쇄 O/X */}
            <div className="mos-row mos-pinned" style={{ gridTemplateColumns: gridCols }}>
              <span className="mos-name"><span className="mos-nm-in">호출</span></span>
              <span className="mos-cell" />
              <Cell on={staffCallUi} onClick={() => toggleStoreUi('call')} />
              {printers.map((pr) => (
                <Cell key={`c-${pr.id}`} on={!!pr.print_call} onClick={() => togglePrinterFlag(pr, 'call')} />
              ))}
            </div>
            <div className="mos-row mos-pinned" style={{ gridTemplateColumns: gridCols }}>
              <span className="mos-name"><span className="mos-nm-in">리뷰</span></span>
              <span className="mos-cell" />
              <Cell on={reviewUi} onClick={() => toggleStoreUi('review')} />
              {printers.map((pr) => (
                <Cell key={`r-${pr.id}`} on={!!pr.print_review} onClick={() => togglePrinterFlag(pr, 'review')} />
              ))}
            </div>

            {/* 카테고리별 메뉴 */}
            {totalMenus === 0 ? (
              <div className="mos-empty">표시할 메뉴가 없습니다</div>
            ) : (
              categories.map((cat) => (
                (cat.menus && cat.menus.length > 0) && (
                  <div key={cat.category_code}>
                    <div className="mos-cat">{cat.category_name || '기타'}</div>
                    {cat.menus.map((menu, i) => {
                      const autopost = !isWowpos && !menu.receipt_yn; // 와우포스는 항상 X
                      const showUi = menu.show_in_ui !== false;
                      const prSet = menuPrinterMap.get(String(menu.menu_key));
                      return (
                        <div className="mos-row" style={{ gridTemplateColumns: gridCols }} key={`${cat.category_code}:${menu.menu_key}:${i}`}>
                          <span className="mos-name" title={menu.menu_name}><span className="mos-nm-in">{menu.menu_name}</span></span>
                          {isWowpos ? <span className="mos-cell" /> : <Cell on={autopost} onClick={() => toggleAutopost(menu)} />}
                          <Cell on={showUi} onClick={() => toggleShowInUi(menu)} />
                          {printers.map((pr) => (
                            <Cell key={`m-${pr.id}`} on={!!prSet?.has(pr.id)} onClick={() => toggleMenuPrinter(menu, pr)} />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )
              ))
            )}
           </div>
          </div>
        )}

        {toast.show && (
          <div className={`mos-toast ${toast.type}`}>{toast.message}</div>
        )}
      </div>

      <style jsx>{`
        .mos-overlay {
          position: fixed; inset: 0; z-index: 10001;
          background: rgba(0,0,0,0.35);
          display: flex; justify-content: center; align-items: flex-end;
          opacity: 0; transition: opacity 0.2s ease;
        }
        .mos-overlay.open { opacity: 1; }
        .mos-sheet {
          width: 100%; max-width: 480px; height: 92vh;
          background: #F7F8FA; border-radius: 20px 20px 0 0;
          display: flex; flex-direction: column;
          transform: translateY(100%); transition: transform 0.2s ease;
          overflow: hidden;
        }
        .mos-overlay.open .mos-sheet { transform: translateY(0); }
        .mos-header {
          display: flex; align-items: center; gap: 8px;
          padding: 16px; background: #fff; border-bottom: 1px solid #EDEFF2;
        }
        .mos-back { background: none; border: none; padding: 4px; cursor: pointer; display: flex; }
        .mos-back img { width: 22px; height: 22px; }
        .mos-title { font-size: 17px; font-weight: 700; color: #222; }
        .mos-loading, .mos-empty { padding: 40px 16px; text-align: center; color: #9AA1AA; font-size: 14px; }
        /* 세로 스크롤 + 프린터 컬럼 많으면 가로 스크롤 */
        .mos-tablewrap { flex: 1; overflow-y: auto; overflow-x: auto; padding: 0 12px 32px; }
        .mos-table { width: 100%; }
        .mos-cat {
          font-size: 12.5px; font-weight: 800; color: #4B5560;
          padding: 14px 6px 6px; letter-spacing: -0.2px;
        }
        /* grid-template-columns 는 인라인 style 로 동적 주입(프린터 N개). 여기선 나머지만. */
        .mos-row {
          display: grid;
          align-items: center; gap: 4px;
          /* 좌측 패딩 0 — 메뉴명 셀이 카드 왼쪽 끝(=sticky left:0 지점)에서 시작해
             가로 스크롤 시 좌측 이동거리 0. 텍스트 들여쓰기는 .mos-name padding-left 로. */
          padding: 12px 8px 12px 0; background: #fff; border-radius: 10px; margin-bottom: 6px;
        }
        .mos-head {
          background: #F7F8FA; margin-bottom: 4px; padding: 12px 8px 12px 0;
          position: sticky; top: 0; z-index: 3; border-radius: 0;
          box-shadow: 0 2px 4px -2px rgba(0,0,0,0.08);
        }
        .mos-head .mos-name, .mos-head .mos-col {
          font-size: 12px; font-weight: 700; color: #8A929B; text-align: center;
        }
        .mos-head .mos-name { text-align: left; }
        .mos-pinned { background: #F0F5FF; box-shadow: inset 0 0 0 1px #DCE7FB; }
        .mos-name {
          font-size: 14px; color: #2A2F36; font-weight: 600;
          display: flex; align-items: center; min-width: 0; padding-left: 12px;
        }
        .mos-nm-in { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
        .mos-col { text-align: center; }
        /* 프린터 2개+ 가로 스크롤 시 메뉴명 열을 좌측 고정 — 나머지 컬럼만 좌우 이동.
           align-self: stretch 로 셀 배경이 행 전체 높이를 덮어 뒤로 스크롤되는 셀을 완전히 가린다. */
        .mos-scroll .mos-name {
          position: sticky; left: 0; z-index: 2; background: #fff;
          align-self: stretch;
        }
        .mos-scroll .mos-head .mos-name { background: #F7F8FA; z-index: 4; }
        .mos-scroll .mos-pinned .mos-name { background: #F0F5FF; }
        .mos-scroll .mos-cat { position: sticky; left: 0; width: -moz-fit-content; width: fit-content; z-index: 2; }
        .mos-cell {
          display: inline-flex; align-items: center; justify-content: center;
          width: 40px; height: 30px; margin: 0 auto; border-radius: 8px;
          font-size: 14px; font-weight: 700; font-family: inherit;
        }
        .mos-cell.on { color: #1B9E5A; }
        .mos-cell.off { color: #E5484D; }
        .mos-clickable {
          border: 1.5px solid transparent; cursor: pointer; background: #F2F4F7;
        }
        .mos-clickable.on { background: #E6F6EE; border-color: #B7E6CC; }
        .mos-clickable.off { background: #FCEBEB; border-color: #F1CFCF; }
        .mos-clickable:active { transform: scale(0.94); }
        .mos-locked, .mos-readonly { background: transparent; cursor: default; opacity: 0.85; }
        .mos-toast {
          position: absolute; left: 50%; bottom: 24px; transform: translateX(-50%);
          padding: 10px 18px; border-radius: 20px; color: #fff; font-size: 13px; font-weight: 600;
          background: #333; box-shadow: 0 4px 16px rgba(0,0,0,0.2); white-space: nowrap;
        }
        .mos-toast.error { background: #D64545; }
      `}</style>
    </div>
  );
};

export default MenuOutputSettings;
